import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import dayjs from 'shared/helpers/dayjs';
import routesConfig from 'constants/routesConfig.json';
import { Event, EventTimelineNote } from 'events/_types';
import { usePosthogCapture } from 'events/posthogCapture/_index';
import { useEvent } from 'events/hooks/useEvent';

import formatDateParam from 'events/helpers/dateFormat';

interface UseEventsSelectionOptions {
  events: Event[];
  isDesktopView: boolean;
  summaryExpanded: boolean;
  selectedDate: dayjs.Dayjs;
  setSelectedDate: (date: dayjs.Dayjs) => void;
  setSummaryExpanded: (next: boolean) => void;
  setEditingPreferences: (next: boolean) => void;
}

export interface EventSelectOptions {
  autoStartRecordingForQuestionId?: string;
}

interface UseEventsSelectionResult {
  selectedEventId: string | null;
  autoStartRecordingForQuestionId: string | null;
  setSelectedEventId: (id: string | null) => void;
  setAutoStartRecordingForQuestionId: (id: string | null) => void;
  handleDateSelect: (date: Date) => void;
  handleEventSelect: (eventId: string, options?: EventSelectOptions) => void;
  handleSearchEventSelect: (event: Event) => void;
  handleSearchNoteSelect: (event: Event, note: EventTimelineNote) => void;
}

export default function useEventsSelection({
  events,
  isDesktopView,
  summaryExpanded,
  selectedDate,
  setSelectedDate,
  setSummaryExpanded,
  setEditingPreferences,
}: UseEventsSelectionOptions): UseEventsSelectionResult {
  const history = useHistory();
  const capture = usePosthogCapture();
  const eventMatch = useRouteMatch<{ eventId: string }>({
    path: `${routesConfig.EVENT_PAGE}/:eventId`,
    exact: true,
  });
  const selectedEventId = eventMatch?.params.eventId ?? null;
  const { data: selectedEvent } = useEvent(
    isDesktopView ? selectedEventId ?? '' : ''
  );

  const setSelectedEventId = useCallback(
    (eventId: string | null) => {
      const pathname = eventId
        ? `${routesConfig.EVENT_PAGE}/${eventId}`
        : routesConfig.EVENTS_PAGE;
      if (pathname === history.location.pathname) return;
      history.replace({ pathname, search: history.location.search });
    },
    [history]
  );
  const [autoStartRecordingForQuestionId, setAutoStartRecordingForQuestionId] =
    useState<string | null>(null);
  const searchSelectionRef = useRef(false);
  const shownInListRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isDesktopView || !selectedEventId) return;
    if (events.some((e) => e.id === selectedEventId)) {
      shownInListRef.current = selectedEventId;
      return;
    }
    if (searchSelectionRef.current) return;
    if (summaryExpanded) return;
    if (shownInListRef.current === selectedEventId) {
      setSelectedEventId(null);
    }
  }, [
    events,
    isDesktopView,
    selectedEventId,
    setSelectedEventId,
    summaryExpanded,
  ]);

  useEffect(() => {
    if (!isDesktopView || !selectedEvent) return;
    if (shownInListRef.current === selectedEvent.id) return;
    const eventDate = dayjs(selectedEvent.modified).startOf('day');
    if (!eventDate.isSame(selectedDate, 'day')) setSelectedDate(eventDate);
  }, [isDesktopView, selectedEvent, selectedDate, setSelectedDate]);

  const handleDateSelect = (date: Date) => {
    searchSelectionRef.current = false;
    setSelectedDate(dayjs(date).startOf('day'));
    setSelectedEventId(null);
    capture.eventsHomeDateSelected({ date: formatDateParam(dayjs(date)) });
  };

  const handleEventSelect = (eventId: string, options?: EventSelectOptions) => {
    searchSelectionRef.current = false;
    capture.eventsHomeEventSelected({ event_id: eventId });
    if (isDesktopView) {
      setSummaryExpanded(false);
      setEditingPreferences(false);
      setSelectedEventId(eventId);
      setAutoStartRecordingForQuestionId(
        options?.autoStartRecordingForQuestionId ?? null
      );
    } else {
      history.push({
        pathname: `${routesConfig.EVENT_PAGE}/${eventId}`,
        state: {
          eventsDate: formatDateParam(selectedDate),
          eventsSearch: history.location.search,
          ...(options?.autoStartRecordingForQuestionId
            ? {
                autoStartRecordingForQuestionId:
                  options.autoStartRecordingForQuestionId,
              }
            : {}),
        },
      });
    }
  };

  const handleSearchEventSelect = (event: Event) => {
    if (isDesktopView) {
      searchSelectionRef.current = true;
      setSelectedDate(dayjs(event.modified).startOf('day'));
      setSelectedEventId(event.id);
      setAutoStartRecordingForQuestionId(null);
    } else {
      history.push({
        pathname: `${routesConfig.EVENT_PAGE}/${event.id}`,
        state: {
          eventsDate: formatDateParam(dayjs(event.modified).startOf('day')),
        },
      });
    }
  };

  const handleSearchNoteSelect = (event: Event, note: EventTimelineNote) => {
    const noteDate = dayjs(note.created).startOf('day');
    if (isDesktopView) {
      searchSelectionRef.current = true;
      setSelectedDate(noteDate);
      setSelectedEventId(event.id);
      setAutoStartRecordingForQuestionId(null);
    } else {
      history.push({
        pathname: `${routesConfig.EVENT_PAGE}/${event.id}`,
        state: {
          eventsDate: formatDateParam(noteDate),
        },
      });
    }
  };

  return {
    selectedEventId,
    autoStartRecordingForQuestionId,
    setSelectedEventId,
    setAutoStartRecordingForQuestionId,
    handleDateSelect,
    handleEventSelect,
    handleSearchEventSelect,
    handleSearchNoteSelect,
  };
}
